import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useProfileImage } from './useProfileImage';
import { useQueryClient } from '@tanstack/react-query';

const fetchSubscriptionData = async () => {
  const authData = localStorage.getItem('auth');
  if (!authData) throw new Error("User not authenticated");

  let parsedAuth;
  try {
    parsedAuth = JSON.parse(authData);
  } catch (error) {
    throw new Error("Invalid authentication data");
  }

  const { token, id } = parsedAuth;
  const response = await axios.get(
    `${process.env.REACT_APP_API_URL}/api/plans`,
    {
      params: { user_id: id, guest: false },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (response.data.status === 200) {
    return response.data;
  } else if (response.data.status === 401) {
    throw new Error("Unauthorized");
  } else {
    throw new Error(response.data.message || "Failed to fetch data");
  }
};
const AuthRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [verifyEmail, setVerifyEmail] = useState('');
  const { profileImage, setProfileImage } = useProfileImage();
  const location = useLocation();
  const navigate = useNavigate();
  const [checkTemp, setCheckTemp] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    const processCheckout = async () => {
      const searchParams = new URLSearchParams(location.search);
      const sessionId = searchParams.get('session_id');
      const app_url = `${process.env.REACT_APP_BASE_URL}/plans`;
      const sDataE = searchParams.get('sDataE');
      const guest = searchParams.get('guest');
      const user_email = searchParams.get('user_email');
      const action = searchParams.get('action');

      // Retrieve token and id from localStorage
      const authData = localStorage.getItem('auth');
      const token = authData ? JSON.parse(authData).token : null;
      const id = authData ? JSON.parse(authData).id : null;

      if (guest == 'true') {
        if (sessionId && action) {
          if (action === 'success') {
            setCheckTemp(true);
            try {
              const fetchUser = await axios.post(
                `${process.env.REACT_APP_API_URL}/api/subscription/success`,
                {
                  session_id: sessionId,
                  app_url: app_url,
                  sDataE: sDataE,
                  user_email: user_email,
                  guest: true,
                },
              );
              if (fetchUser.data.status === 200) {
                localStorage.setItem('auth', JSON.stringify(fetchUser.data.user));
                navigate('/');
              }
            } catch (error) {
              console.error('Error fetching user info:', error);
              localStorage.removeItem('auth');
              setIsAuthenticated(false);
              navigate('login');
            } finally {
              setIsLoading(false);
              setCheckTemp(false);
            }
          }
        }
      }
    };

    processCheckout();
  }, [location, navigate]);

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const authData = localStorage.getItem('auth');
        const token = authData ? JSON.parse(authData).token : null;
        if (!token) {
          localStorage.removeItem('auth');
          setIsAuthenticated(false);
          setIsLoading(false);
          return;
        }

        const aData = JSON.parse(authData);
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/api/user-info`,
          {
            user_id: aData.id,
          },
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.data.status === 200) {
          const authData = localStorage.getItem('auth');
          const isAuthenticated = authData ? JSON.parse(authData).token : null;
          const userAid = authData ? JSON.parse(authData).id : null;
          response.data.user.token = isAuthenticated
          localStorage.setItem('auth', JSON.stringify(response.data.user));
          setIsAuthenticated(true);
          setProfileImage(response.data.user.image);
          //localStorage.setItem('profileImage'+userAid, response.data.user.image);
          const emailVerifiedAt = response.data.user.email_verified_at;
          setVerifyEmail(emailVerifiedAt)

          queryClient.prefetchQuery({
            queryKey: ['subscriptionData'],
            queryFn: () => fetchSubscriptionData(token, aData.id),
            staleTime: Infinity,
            cacheTime: Infinity,
          }).catch((error) => {
            console.error('Background subscription fetch failed:', error);
          });
        }
      } catch (error) {
        console.error('Error fetching user info:', error);
        localStorage.removeItem('auth');
        setIsAuthenticated(false);
        navigate('login')
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserInfo();
  }, [location, navigate, setProfileImage, queryClient]);

  if (isLoading) {
    return;
  }

  if (checkTemp) {
    return (
      <div className="container" style={{ marginTop: '70px' }}>
        <div className="row justify-content-center">
          <div className="col-md-5 mt-5">
            <div className="message-box _success shadow-lg">
              <div className="payment-loader mb-3">
                <div className="pad">
                  <div className="chip"></div>
                  <div className="line line1"></div>
                  <div className="line line2"></div>
                </div>
              </div>
              <h3 className="mt-5">Please wait while payment is loading</h3>
              <p>Thank you for your payment. We will be in contact with more details shortly.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }
  return isAuthenticated ? children : <Navigate to="/login" />;
};

export default AuthRoute;
